/**
 * User Action Modal - User Timeline Version
 * Simplified version without user information section
 */

import { useEffect, useState, memo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Close, Event as EventIcon } from '@mui/icons-material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import axios from 'src/utilities/http-client'
import { useAppDispatch } from 'src/store/hooks'
import {
  successMessageChanged,
  errorMessageChanged,
} from 'src/v3/manage/manage.slice'
import CopyToClipboardV4 from 'src/v3/manage/components/copy-to-clipboard'
import type { RosterUserActionData } from '../../types'
import NotificationSnackbars from './notification-snackbars'

dayjs.extend(relativeTime)

// Session response type based on API documentation
interface EventSessionResponse {
  id: string
  name: string
  eventID: string
  sessionTime: string
  endTime?: string
  createdTime?: string
  updatedTime?: string
}

interface UserActionModalProps {
  event: RosterUserActionData | null
  isOpen: boolean
  onClose: () => void
  linkedServiceID: string
}

let UserActionModal = memo(
  ({ event, isOpen, onClose }: UserActionModalProps) => {
    let dispatch = useAppDispatch()

    // State management
    let [sessionData, setSessionData] = useState<EventSessionResponse | null>(
      null,
    )
    let [isLoadingSession, setIsLoadingSession] = useState(false)
    let [sessionError, setSessionError] = useState('')

    // Fetch session details
    useEffect(() => {
      if (isOpen && event?.sessionID) {
        setIsLoadingSession(true)
        setSessionError('')
        setSessionData(null)

        axios
          .get(`/manage/dashboard/session/${event.sessionID}`)
          .then(({ data }) => {
            setSessionData(data)
          })
          .catch((error) => {
            setSessionError(
              error.response?.data?.message || 'Failed to load session details',
            )
          })
          .finally(() => {
            setIsLoadingSession(false)
          })
      }
    }, [isOpen, event?.sessionID])

    // Reset error and messages when modal closes
    useEffect(() => {
      if (!isOpen) {
        setSessionError('')
        dispatch(successMessageChanged(''))
        dispatch(errorMessageChanged(''))
      }
    }, [isOpen, dispatch])

    if (!event) {
      return null
    }

    // Format action type for display
    let formatActionType = (actionType: string) => {
      switch (actionType) {
        case 'event-attended':
          return 'Event Attended'
        case 'event-completed':
          return 'Event Completed'
        default:
          return actionType
            .replace(/-/g, ' ')
            .replace(/\b\w/g, (l) => l.toUpperCase())
      }
    }

    return (
      <Dialog
        open={isOpen}
        onClose={onClose}
        aria-labelledby="user-action-modal-title"
        slotProps={{
          paper: {
            sx: {
              width: '80vw',
              height: '80vh',
              maxWidth: 'none',
              maxHeight: 'none',
              borderRadius: 2,
              bgcolor: 'background.default',
            },
          },
        }}
      >
        <DialogTitle>
          <Box className="flex items-center justify-between">
            <Box className="flex items-center gap-4">
              <Typography variant="h6" id="user-action-modal-title">
                User Action Details
              </Typography>
              <Chip
                label={formatActionType(event.actionType)}
                sx={{
                  '&.MuiChip-root': {
                    backgroundColor: (theme) =>
                      `${theme.palette.entityColors.userAction}20`,
                    color: (theme) => theme.palette.entityColors.userAction,
                  },
                  fontWeight: 500,
                }}
              />
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close dialog"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className="p-4">
          <Box className="mb-4">
            <Typography variant="body2" color="text.secondary" className="mb-1">
              {dayjs(event.actionDate).format('MMMM D, YYYY [at] h:mm A')} •{' '}
              {dayjs(event.actionDate).fromNow()}
            </Typography>
            <Typography variant="body2" className="font-medium">
              {event.eventName}
            </Typography>
          </Box>

          <Box className="grid grid-cols-1 gap-4 mb-4 lg:grid-cols-2">
            <Paper
              sx={{
                p: 2,
                bgcolor: 'background.paperDark',
              }}
              className="flex flex-col"
            >
              <Typography variant="h6" className="mb-4 flex items-center gap-2">
                <EventIcon />
                Session Details
              </Typography>

              {isLoadingSession ? (
                <Box
                  className="flex justify-center py-8"
                  role="status"
                  aria-live="polite"
                  aria-label="Loading session details"
                >
                  <CircularProgress size={40} />
                </Box>
              ) : sessionError ? (
                <Alert severity="error" className="mb-4" role="alert">
                  {sessionError}
                </Alert>
              ) : sessionData ? (
                <Box className="flex-1">
                  <Box className="mb-6">
                    <Typography variant="body2" color="text.secondary">
                      Session Name
                    </Typography>
                    <Typography variant="body1" className="font-medium">
                      {sessionData.name || 'Unnamed Session'}
                    </Typography>
                  </Box>

                  <Box className="grid grid-cols-1 gap-3">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Session Time
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(sessionData.sessionTime).format(
                          'MMM D, YYYY [at] h:mm A',
                        )}
                      </Typography>
                    </Box>

                    {sessionData.endTime && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          End Time
                        </Typography>
                        <Typography variant="body2">
                          {dayjs(sessionData.endTime).format(
                            'MMM D, YYYY [at] h:mm A',
                          )}
                        </Typography>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Duration
                      </Typography>
                      <Typography variant="body2">
                        {sessionData.endTime
                          ? `${dayjs(sessionData.endTime).diff(dayjs(sessionData.sessionTime), 'minutes')} minutes`
                          : 'Zero hour event'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="mb-1"
                      >
                        Event ID
                      </Typography>
                      <CopyToClipboardV4
                        value={sessionData.eventID || event.eventID}
                        label="Event ID"
                      />
                    </Box>
                  </Box>
                </Box>
              ) : event.sessionID ? (
                <Box className="py-4 text-center">
                  <Typography variant="body2" color="text.secondary">
                    Session information not available
                  </Typography>
                </Box>
              ) : (
                <Box className="py-4 text-center">
                  <Typography variant="body2" color="text.secondary">
                    No session associated with this action
                  </Typography>
                </Box>
              )}
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
              <Typography variant="h6" className="mb-4">
                Action Details
              </Typography>

              <Box className="grid grid-cols-1 gap-1">
                <Box className="p-2">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    className="block mb-1"
                  >
                    Action ID
                  </Typography>
                  <CopyToClipboardV4 value={event.actionID} label="Action ID" />
                </Box>

                <Box className="p-2">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    className="block"
                  >
                    Action Type
                  </Typography>
                  <Typography variant="body2" className="font-medium">
                    {formatActionType(event.actionType)}
                  </Typography>
                </Box>

                <Box className="p-2">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    className="block mb-1"
                  >
                    Event ID
                  </Typography>
                  <CopyToClipboardV4 value={event.eventID} label="Event ID" />
                </Box>

                {event.sessionID && (
                  <Box className="p-2">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      className="block mb-1"
                    >
                      Session ID
                    </Typography>
                    <CopyToClipboardV4
                      value={event.sessionID}
                      label="Session ID"
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions className="p-4 gap-2">
          <Button onClick={onClose} autoFocus>
            Close
          </Button>
        </DialogActions>
        <NotificationSnackbars />
      </Dialog>
    )
  },
)

export default UserActionModal
